import type { FetchTicketResponse } from 'src/api/models';
import type { MechanicWorkload } from 'src/types/trello-board-type';

export interface ColumnWorkloadResult {
  workload: number | null;
  workloadOverflow: number;
  isUnplanned: boolean;
  unassignedHours: number;
  missingDurationCount: number;
  mechanicBreakdown: MechanicWorkload[];
}

/**
 * Parses the runtime shape of expected_duration.
 * The generated OpenAPI type is `string[] | null` but at runtime the API
 * returns `{ hours?: number; minutes?: number }`.
 * Returns the total duration in fractional hours, or 0 for any unrecognised shape.
 */
export function parseDuration(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value !== 'object' || Array.isArray(value)) return 0;
  const obj = value as Record<string, unknown>;
  if (!('hours' in obj) && !('minutes' in obj)) return 0;
  const hours = typeof obj.hours === 'number' ? obj.hours : 0;
  const minutes = typeof obj.minutes === 'number' ? obj.minutes : 0;
  return hours + minutes / 60;
}

/**
 * Calculates the workload result for a single day column.
 * - Groups tickets by assigned mechanic
 * - Unassigned tickets are tracked separately (unassignedHours)
 * - A column with no mechanics is "unplanned" (workload = null)
 */
export function calcColumnWorkload(tickets: FetchTicketResponse[]): ColumnWorkloadResult {
  let missingDurationCount = 0;
  let unassignedHours = 0;
  // mechanic id -> { name, hours }
  const mechanicMap = new Map<string, { name?: string; hours: number }>();

  for (const ticket of tickets) {
    const hasDuration =
      ticket.expected_duration !== null &&
      ticket.expected_duration !== undefined;
    if (!hasDuration) {
      missingDurationCount++;
    }
    const hours = parseDuration(ticket.expected_duration as unknown);

    const assigneeKey = ticket.assignee?.id ?? ticket.assignee?.name;
    if (assigneeKey) {
      const existing = mechanicMap.get(assigneeKey);
      if (existing) {
        existing.hours += hours;
      } else {
        mechanicMap.set(assigneeKey, { name: ticket.assignee?.name, hours });
      }
    } else {
      unassignedHours += hours;
    }
  }

  if (mechanicMap.size === 0) {
    return {
      workload: null,
      workloadOverflow: 0,
      isUnplanned: true,
      unassignedHours,
      missingDurationCount,
      mechanicBreakdown: [],
    };
  }

  const mechanicBreakdown: MechanicWorkload[] = [];
  let totalAssignedHours = 0;

  for (const [id, { name, hours }] of mechanicMap.entries()) {
    totalAssignedHours += hours;
    mechanicBreakdown.push({
      id,
      name,
      scheduledHours: hours,
      percentage: (hours / 8) * 100, // uncapped per-mechanic percentage
    });
  }

  const capacity = mechanicMap.size * 8;
  const rawPercent = (totalAssignedHours / capacity) * 100;
  const workload = Math.min(Math.round(rawPercent), 100);
  const workloadOverflow = rawPercent > 100 ? Math.round(rawPercent - 100) : 0;

  return {
    workload,
    workloadOverflow,
    isUnplanned: false,
    unassignedHours,
    missingDurationCount,
    mechanicBreakdown,
  };
}

/**
 * Calculates the aggregate workload for a week separator column.
 * Averages the non-null (planned) day workload percentages.
 */
export function calcSeparatorWorkload(
  dayWorkloads: (number | null)[],
): { workload: number | null; workloadOverflow: number; isUnplanned: boolean } {
  const nonNull = dayWorkloads.filter((v): v is number => v !== null);

  if (nonNull.length === 0) {
    return { workload: null, workloadOverflow: 0, isUnplanned: true };
  }

  const sum = nonNull.reduce((acc, v) => acc + v, 0);
  const average = sum / nonNull.length;
  const workload = Math.min(Math.round(average), 100);
  const workloadOverflow = average > 100 ? Math.round(average - 100) : 0;

  return { workload, workloadOverflow, isUnplanned: false };
}
